import type {
  SeedanceProviderConfigView,
  SeedreamImageGenerationView,
  StoryListItem,
} from "@actale/contracts";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api, ApiError, storyImageUrl } from "./api.js";
import { formatUpdatedAt } from "./story-utils.js";

const LIGHTHOUSE_STORY_SLUG = "mist-over-lighthouse";
const MODEL_NAME = "doubao-seedream-5-0-260128";
const LISTED_UNIT_PRICE_RMB = "0.22";
export const MIA_ORIGINAL_PORTRAIT_PROMPT = "原创虚构成年女性角色“米娅”，不得模仿任何真实人物、明星或已知影视角色。写实电影摄影，不是插画，不是卡通，不是3D。竖屏9:16，单人中近景，正面略微三分之四侧身，完整显示头部到胸口，双眼和嘴部清晰，适合后续口型驱动。她是一名年轻气象研究员，黑色齐肩直发，左眉尾有一道很浅的断痕，鼻梁左侧有三颗浅雀斑，肤质真实自然，五官有辨识度但不过度精修。穿深灰蓝色防风外套，胸前有小型黄铜气压计挂件，不出现品牌和文字。补给船即将靠近被浓雾包围的灯塔岛，她克制专业但明显担心失联三天的林叔，嘴唇自然闭合，表情紧张、不哭。自然阴天冷光，轻微海雾背景，浅景深，真实镜头质感，人物面部无变形、无多余人物、无文字、无水印、无标志。";

const statusLabels: Record<SeedreamImageGenerationView["status"], string> = {
  SUBMITTING: "提交中，结果待确认",
  SUCCEEDED: "已生成 · 待评审",
  FAILED: "失败",
};

const errorLabels: Record<string, string> = {
  SEEDREAM_PROVIDER_AUTH_FAILED: "火山方舟密钥或模型权限无效，请检查 AI 模型设置。",
  SEEDREAM_PROVIDER_QUOTA_OR_RATE_LIMIT: "账号额度不足或请求过于频繁，请先检查火山账单和额度。",
  SEEDREAM_PROVIDER_REJECTED: "供应商拒绝了提示词，请检查内容合规后再决定是否重试。",
  SEEDREAM_SUBMISSION_OUTCOME_UNKNOWN: "网络中断，是否已计费和生成无法确认。系统不会自动重试，请先检查火山控制台。",
  SEEDREAM_PROVIDER_INVALID_RESPONSE: "供应商返回内容无法识别。",
  SEEDREAM_PROVIDER_EMPTY_IMAGE: "供应商没有返回可用图片。",
  SEEDREAM_IMAGE_MEDIA_INVALID: "返回文件不是可验证的 JPEG 图片。",
  SEEDREAM_IMAGE_MEDIA_TOO_LARGE: "返回图片超过 20MB。",
  SEEDREAM_IMAGE_GENERATION_FAILED: "生成或转存图片失败。",
};

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

function replaceGeneration(
  current: SeedreamImageGenerationView[],
  next: SeedreamImageGenerationView,
): SeedreamImageGenerationView[] {
  return [next, ...current.filter((item) => item.id !== next.id)];
}

export function SeedreamImagePage({
  onConfigureProvider,
}: {
  onConfigureProvider?: () => void;
}): React.ReactNode {
  const [config, setConfig] = useState<SeedanceProviderConfigView | null>(null);
  const [stories, setStories] = useState<StoryListItem[]>([]);
  const [generations, setGenerations] = useState<SeedreamImageGenerationView[]>([]);
  const [storyId, setStoryId] = useState("");
  const [label, setLabel] = useState("");
  const [prompt, setPrompt] = useState("");
  const [costConfirmed, setCostConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextConfig, storyResult, nextGenerations] = await Promise.all([
        api.seedanceProviderConfig(),
        api.storyLines({ pageSize: 100 }),
        api.seedreamImageGenerations(),
      ]);
      setConfig(nextConfig);
      setStories(storyResult.data);
      setGenerations(nextGenerations);
      setStoryId((current) => current
        || storyResult.data.find((story) => story.slug === LIGHTHOUSE_STORY_SLUG)?.id
        || storyResult.data[0]?.id
        || "");
    } catch (caught) {
      setError(message(caught, "无法读取图片生成工作台"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const promptBytes = useMemo(() => new TextEncoder().encode(prompt.trim()).length, [prompt]);
  const selectedStory = stories.find((story) => story.id === storyId) ?? null;
  const ready = Boolean(
    config?.configured
    && storyId
    && label.trim()
    && prompt.trim()
    && prompt.trim().length <= 4_000
    && promptBytes <= 12_000
    && costConfirmed
    && !submitting,
  );

  function fillMiaPrompt(): void {
    setLabel("米娅原创人物图 · 第一版");
    setPrompt(MIA_ORIGINAL_PORTRAIT_PROMPT);
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || !selectedStory) return;
    if (!window.confirm(`确认付费生成“${label.trim()}”？\n\n故事线：《${selectedStory.title}》\n模型：${MODEL_NAME}\n数量：1 张\n规格：2K JPEG\n官网当前单价：最多按 ${LISTED_UNIT_PRICE_RMB} 元/张计费\n\n本次会把提示词发送给火山方舟。实际免费额度或账单以账号为准；系统不会自动重试，也不会修改故事草稿或发布 StoryVersion。`)) return;
    setSubmitting(true);
    setError("");
    setNotice("");
    try {
      const next = await api.createSeedreamImageGeneration({
        clientRequestId: crypto.randomUUID(),
        storyId,
        label: label.trim(),
        prompt: prompt.trim(),
        costConfirmed: true,
      });
      setGenerations((current) => replaceGeneration(current, next));
      setCostConfirmed(false);
      if (next.status === "SUCCEEDED") {
        setNotice("原创人物图已生成并转存到 ACTALE，当前只是待评审候选；故事草稿和 StoryVersion 均未修改。");
      } else {
        setError(errorLabels[next.errorCode ?? ""] ?? "图片生成失败，请查看下方任务记录。");
      }
    } catch (caught) {
      setError(message(caught, "图片生成提交失败"));
      try {
        setGenerations(await api.seedreamImageGenerations());
      } catch {
        // Preserve the original actionable submission error.
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading && !config) {
    return <div className="director-loading"><span /><strong>正在读取图片生成工作台…</strong></div>;
  }
  if (!config) {
    return <div className="state-card"><div className="error-notice" role="alert"><span>!</span>{error || "图片生成工作台不可用"}</div><button className="secondary-button" onClick={() => void load()}>重新加载</button></div>;
  }

  return (
    <div className="image-generation-page">
      <div className="page-heading provider-config-heading">
        <div>
          <p className="eyebrow">AI IMAGE STUDIO</p>
          <h1>图片生成</h1>
          <p>从零生成可留存来源的原创人物候选；先评审人物，再决定是否交给口型视频模型。</p>
        </div>
        <button className="secondary-button" disabled={loading || submitting} onClick={() => void load()}>{loading ? "正在刷新…" : "刷新工作台"}</button>
      </div>

      {error ? <div className="error-notice" role="alert"><span>!</span>{error}</div> : null}
      {notice ? <div className="director-notice" role="status">✓ {notice}</div> : null}

      <form className="image-generation-form" onSubmit={(event) => void submit(event)}>
        <header>
          <div>
            <span className={`provider-state-dot ${config.configured ? "ready" : "empty"}`} />
            <div><strong>新建原创人物图</strong><small>复用火山方舟 Key · Seedream 5.0 Lite · 单张 2K JPEG</small></div>
          </div>
          <span className="provider-config-status ready">不会修改故事</span>
        </header>
        {!config.configured ? <div className="tts-config-required" role="alert"><div><strong>火山方舟尚未配置</strong><span>请先在“AI 模型设置”中保存方舟 API 密钥。</span></div><button type="button" className="secondary-button" onClick={onConfigureProvider}>前往 AI 模型设置</button></div> : null}
        <div className="image-generation-grid">
          <label>
            <span>关联故事线</span>
            <select value={storyId} onChange={(event) => setStoryId(event.target.value)} required>
              <option value="">请选择故事线</option>
              {stories.map((story) => <option key={story.id} value={story.id}>{story.title} · 草稿 R{story.draftRevision}</option>)}
            </select>
          </label>
          <label>
            <span>任务名称</span>
            <input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={120} placeholder="例如：米娅原创人物图 · 第一版" required />
          </label>
          <label className="image-prompt-field">
            <span className="tts-field-heading"><span>人物图提示词</span>{selectedStory?.slug === LIGHTHOUSE_STORY_SLUG ? <button type="button" className="secondary-button" onClick={fillMiaPrompt}>填入米娅原创方案</button> : null}</span>
            <textarea value={prompt} onChange={(event) => setPrompt(event.target.value)} rows={10} maxLength={4_000} placeholder="只描述原创虚构人物，不要使用真人、明星或公共人像素材。" required />
            <small className={promptBytes > 12_000 ? "invalid" : ""}>{prompt.trim().length} / 4,000 字符 · {promptBytes} / 12,000 字节</small>
          </label>
        </div>
        <div className="tts-model-snapshot">
          <div><span>模型</span><strong>{MODEL_NAME}</strong></div>
          <div><span>输出</span><strong>1 张 · 2K · JPEG</strong></div>
          <div><span>模型页列价（2026-08-26）</span><strong>最多 {LISTED_UNIT_PRICE_RMB} 元/张</strong></div>
        </div>
        <div className="image-rights-note"><strong>素材边界</strong><span>只生成原创虚构人物；不上传真人、明星、公共人像库或其他模型的人物图。后台会保留模型、提示词、文件哈希和请求记录。</span></div>
        <label className="tts-cost-confirm">
          <input type="checkbox" checked={costConfirmed} onChange={(event) => setCostConfirmed(event.target.checked)} />
          <span><strong>我确认本次只生成 1 张图片，并可能产生最多 {LISTED_UNIT_PRICE_RMB} 元费用</strong><small>实际免费额度或账单以火山账号为准；系统不会自动重试。</small></span>
        </label>
        <footer>
          <span>生成只会新增一张待评审候选；不会自动用于角色头像、视频、故事草稿或 StoryVersion。</span>
          <button className="primary-button compact" disabled={!ready}>{submitting ? "正在生成，请勿重复提交…" : "确认并生成 1 张图片"}</button>
        </footer>
      </form>

      <section className="image-generation-history">
        <header><div><strong>最近图片任务</strong><small>每条任务保留模型、提示词、尺寸、文件哈希和生成结果</small></div><span>{generations.length} 条</span></header>
        {!generations.length ? <div className="empty-state"><span className="empty-glyph" aria-hidden="true">像</span><h2>还没有正式图片任务</h2><p>配置完成不等于已生成；确认首条任务后才可能产生费用和图片。</p></div> : null}
        <div className="image-generation-list">
          {generations.map((generation) => {
            const imageUrl = generation.managedImageKey ? storyImageUrl(generation.managedImageKey) : null;
            return <article className={`image-generation-item ${generation.status.toLowerCase()}`} key={generation.id}>
              <header><span>{statusLabels[generation.status]}</span><div><strong>{generation.label}</strong><small>《{generation.story.title}》 · {formatUpdatedAt(generation.createdAt)}</small></div></header>
              {imageUrl ? <div className="image-result"><img src={imageUrl} alt={`${generation.label}，AI 生成候选图`} loading="lazy" /><div><strong>待人工评审</strong><span>请检查人物辨识度、真实感、面部完整度，以及是否适合后续口型驱动。</span><a className="secondary-button" href={imageUrl} target="_blank" rel="noreferrer">打开或下载 JPEG</a></div></div> : null}
              {generation.status === "FAILED" ? <div className="seedance-job-error"><strong>{generation.errorCode ?? "SEEDREAM_IMAGE_GENERATION_FAILED"}</strong><span>{errorLabels[generation.errorCode ?? ""] ?? "请检查配置、提示词和供应商状态后再决定是否重新提交。"}</span></div> : null}
              {generation.status === "SUBMITTING" ? <div className="tts-pending-note">这条任务没有完成结果确认。系统不会自动重试，避免重复扣费。</div> : null}
              <details><summary>查看生成参数和来源记录</summary><dl>
                <div><dt>模型</dt><dd>{generation.modelName}</dd></div>
                <div><dt>规格</dt><dd>{generation.size} · {generation.outputFormat}</dd></div>
                <div><dt>图片</dt><dd>{generation.managedImageWidth && generation.managedImageHeight ? `${generation.managedImageWidth} × ${generation.managedImageHeight}` : "—"}</dd></div>
                <div><dt>文件</dt><dd>{generation.managedImageBytes ? `${(generation.managedImageBytes / 1024 / 1024).toFixed(2)}MB` : "—"}</dd></div>
                <div className="image-parameter-wide"><dt>SHA-256</dt><dd>{generation.managedImageSha256 ?? "—"}</dd></div>
                <div className="image-parameter-wide"><dt>提示词</dt><dd>{generation.prompt}</dd></div>
              </dl></details>
            </article>;
          })}
        </div>
      </section>
    </div>
  );
}
